import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { useLocalization } from '~/framework';
import { useApiErrorMessageToast } from '~/pages/_shared';
import {
   CreateCourseAssignmentErrorCode,
   createCourseAssignmentFromClient,
   CreateCourseAssignmentOptions,
   CreateCourseAssignmentResult,
} from '~/pages/api/courses/_shared/create-course-assignment-modal/createCourseAssignment.api';
import { getCourseAssignmentKeys } from '../getCourseAssignmentKeys';
import { getCourseKey } from '../useCourse';
import { getCoursesKey } from '../useCourses';

export function useCreateCourseAssignment(
   options: UseMutationOptions<
      CreateCourseAssignmentResult,
      null,
      CreateCourseAssignmentOptions
   > = {}
) {
   const { _js } = useLocalization();
   const queryClient = useQueryClient();
   const courseKey = getCourseKey();
   const coursesKey = getCoursesKey();
   const courseAssignmentKeys = getCourseAssignmentKeys();

   const errorHandler = useApiErrorMessageToast<CreateCourseAssignmentErrorCode>({
      'course_assignments.create.insufficent_privileges': _js(
         'You do not have permission to assign a course'
      ),
      'course_assignments.create.course_not_found': _js('Cannot assign disabled course'),
      'course_assignments.create.course_disabled': _js('Could not find the course selected'),
      default: _js('There was a problem assigning this course, please try again'),
   });

   return useMutation<CreateCourseAssignmentResult, null, CreateCourseAssignmentOptions>(
      createCourseAssignmentFromClient,
      {
         ...options,
         onSuccess(...args) {
            queryClient.invalidateQueries({ queryKey: courseKey });
            queryClient.invalidateQueries({ queryKey: coursesKey });
            courseAssignmentKeys.forEach(key =>
               queryClient.invalidateQueries({
                  queryKey: [key],
               })
            );

            if (options.onSuccess) {
               options.onSuccess(...args);
            }
         },
         onError: errorHandler.handleErrorOrRedirectToLogin,
      }
   );
}
